import { jsPDF } from 'jspdf'
import type { PocRecord, VendorCountryConfig, JiraTicketRef, ExternalRequest, ResultsSummary, CountryResults, VendorPassRate } from '../types'
import { COUNTRIES } from '../data/countries'
import { VENDORS } from '../data/vendorMetadata'
import { MATCH_TYPE_OPTIONS, EXECUTION_METHODS, CONSENT_OPTIONS, CHECK_TYPE_OPTIONS, DISCOVERY_SECTIONS } from '../data/discoveryChecklist'

function countryName(code: string): string {
  return COUNTRIES.find(c => c.code === code)?.name || code
}

function vendorName(id: string): string {
  return VENDORS.find(v => v.id === id)?.name || id
}

/**
 * Export all data on a POC to a comprehensive PDF document.
 */
export function exportPocPDF(poc: PocRecord): void {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 16
  const contentWidth = pageWidth - margin * 2
  let y = 20

  const disc = (poc.discovery_json || {}) as Record<string, unknown>
  const matchCriteria = (poc.match_criteria_json || {}) as Record<string, unknown>
  const vendorConfig = poc.vendor_config_json || {}
  const tickets = poc.jira_tickets_json || []
  const requests = poc.external_requests_json || []
  const results = poc.results_summary_json as ResultsSummary | null

  // ── Helpers ──────────────────────────────────────────────────────────

  function checkPage(needed = 12) {
    if (y > pageHeight - 25 - needed) { doc.addPage(); y = 20 }
  }

  function sectionHeader(title: string) {
    checkPage(20)
    y += 4
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0, 160, 176) // cyan accent
    doc.text(title, margin, y)
    y += 1
    doc.setDrawColor(0, 160, 176)
    doc.setLineWidth(0.4)
    doc.line(margin, y, margin + contentWidth, y)
    doc.setTextColor(0, 0, 0)
    doc.setFont('helvetica', 'normal')
    y += 6
  }

  function subHeader(title: string) {
    checkPage(14)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(44, 62, 80)
    doc.text(title, margin, y)
    doc.setTextColor(0, 0, 0)
    doc.setFont('helvetica', 'normal')
    y += 5
  }

  function field(label: string, value: string | number | null | undefined, fallback = '-') {
    checkPage(10)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text(label, margin, y)
    doc.setFont('helvetica', 'normal')
    const text = value != null && String(value).trim() ? String(value) : fallback
    const labelWidth = doc.getTextWidth(label) + 2
    const remaining = contentWidth - labelWidth
    const lines = doc.splitTextToSize(text, remaining)
    doc.setFontSize(9)
    doc.text(lines[0], margin + labelWidth, y)
    if (lines.length > 1) {
      for (let i = 1; i < lines.length; i++) {
        y += 4
        checkPage(4)
        doc.text(lines[i], margin + labelWidth, y)
      }
    }
    y += 5
  }

  function fieldBlock(label: string, value: string | null | undefined, fallback = '-') {
    checkPage(12)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text(label, margin, y)
    doc.setFont('helvetica', 'normal')
    y += 4
    doc.setFontSize(9)
    const text = value?.trim() || fallback
    const lines = doc.splitTextToSize(text, contentWidth)
    checkPage(lines.length * 4)
    doc.text(lines, margin, y)
    y += lines.length * 4 + 3
  }

  function table(headers: string[], rows: string[][], colWidths: number[]) {
    if (rows.length === 0) return
    checkPage(8 + rows.length * 6)
    const rowHeight = 6
    doc.setFontSize(7.5)

    // Header row
    doc.setFont('helvetica', 'bold')
    doc.setFillColor(235, 235, 235)
    doc.rect(margin, y - 3.5, contentWidth, rowHeight, 'F')
    let x = margin
    for (let i = 0; i < headers.length; i++) {
      doc.text(headers[i], x + 1.5, y)
      x += colWidths[i]
    }
    y += rowHeight
    doc.setFont('helvetica', 'normal')

    // Data rows
    for (const row of rows) {
      checkPage(rowHeight)
      doc.setDrawColor(220, 220, 220)
      doc.line(margin, y - 3.5, margin + contentWidth, y - 3.5)
      x = margin
      for (let i = 0; i < row.length; i++) {
        const cellText = doc.splitTextToSize(row[i] || '', colWidths[i] - 3)
        doc.text(cellText[0] || '', x + 1.5, y)
        x += colWidths[i]
      }
      y += rowHeight
    }
    y += 4
  }

  // ── Title ────────────────────────────────────────────────────────────

  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(0, 160, 176)
  doc.text('POC Report', margin, y)
  y += 8

  doc.setFontSize(14)
  doc.setTextColor(30, 30, 30)
  doc.text(poc.company_name || poc.deal_name, margin, y)
  y += 7

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)
  const statusLabel = poc.status.charAt(0).toUpperCase() + poc.status.slice(1)
  doc.text(`Status: ${statusLabel}  |  Generated: ${new Date().toLocaleDateString('en-AU')}`, margin, y)
  doc.setTextColor(0, 0, 0)
  y += 10

  // ══════════════════════════════════════════════════════════════════════
  // 1. POC Overview
  // ══════════════════════════════════════════════════════════════════════

  sectionHeader('1. POC Overview')

  field('Deal Name:', poc.deal_name)
  field('Company:', poc.company_name)
  field('Status:', statusLabel)
  field('Check Types:', (poc.check_types || []).map(ct => CHECK_TYPE_OPTIONS.find(o => o.id === ct)?.label || ct).join(', '))
  field('Countries:', (poc.countries_in_scope || []).map(c => `${countryName(c)} (${c})`).join(', '))
  field('Record Count:', poc.record_count)
  field('Current Pass Rate:', poc.current_pass_rate != null ? `${poc.current_pass_rate}%` : null)
  field('Success Criteria:', poc.success_criteria)
  field('Consent:', CONSENT_OPTIONS.find(o => o.id === poc.consent_status)?.label || poc.consent_status)
  field('Account Owner:', poc.presales_owner)
  field('Customer Contact:', disc.contact_name as string)
  field('Created:', poc.created_at ? new Date(poc.created_at).toLocaleDateString('en-AU') : null)

  // ══════════════════════════════════════════════════════════════════════
  // 2. Discovery & Intake
  // ══════════════════════════════════════════════════════════════════════

  sectionHeader('2. Discovery & Intake')

  fieldBlock('Problem:', disc.problem as string)
  fieldBlock('What is being tested:', disc.check_types_detail as string)
  fieldBlock('Results format:', disc.results_format as string)
  fieldBlock('Timeline:', disc.timeline as string)
  field('Current Vendor:', disc.current_vendor as string)

  // Per-country targets
  const countryCriteria = (disc.country_criteria || {}) as Record<string, Record<string, string>>
  const countryRows = (poc.countries_in_scope || []).map(code => {
    const c = countryCriteria[code] || {}
    return [
      `${countryName(code)} (${code})`,
      c.current ? `${c.current}%` : '-',
      c.target ? `${c.target}%` : '-',
      c.records || '-',
    ]
  })
  if (countryRows.length > 0) {
    subHeader('Per-Country Targets')
    const w = contentWidth / 4
    table(['Country', 'Current %', 'Target %', 'Records'], countryRows, [w * 1.4, w * 0.8, w * 0.8, w])
  }

  // Data files
  const dataFiles = (disc.data_files || []) as { name: string; country: string; status: string; url: string }[]
  if (dataFiles.length > 0) {
    subHeader('Data Files')
    const fw = contentWidth / 4
    table(
      ['File', 'Country', 'Status', 'URL'],
      dataFiles.map(f => [f.name || '-', f.country || '-', f.status || '-', f.url || '-']),
      [fw * 1.2, fw * 0.6, fw * 0.7, fw * 1.5]
    )
  }

  // Additional discovery sections
  for (const section of DISCOVERY_SECTIONS) {
    if (['define', 'success', 'scope', 'matching'].includes(section.id)) continue
    const hasAnyAnswer = section.questions.some(q => disc[q.id])
    if (!hasAnyAnswer) continue
    subHeader(section.title)
    for (const q of section.questions) {
      const val = disc[q.id] as string
      if (val) fieldBlock(`${q.label}:`, val)
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // 3. Match Criteria
  // ══════════════════════════════════════════════════════════════════════

  sectionHeader('3. Match Criteria')

  const matchType = matchCriteria.match_type as string
  field('Match Type:', MATCH_TYPE_OPTIONS.find(o => o.id === matchType)?.label || matchType)
  field('Address Matching:', matchCriteria.address_partial === 'partial' ? 'Partial allowed' : matchCriteria.address_partial === 'full' ? 'Full match required' : '-')
  field('Fuzziness Notes:', matchCriteria.fuzziness_notes as string)
  fieldBlock('Match Description:', disc.match_description as string)
  field('Vanessa Confirmed:', disc.vanessa_confirmed === 'yes' ? 'Yes' : 'No')

  // Address fields
  const addressFields = ['include_street', 'include_city', 'include_state', 'include_zip']
    .filter(f => matchCriteria[f] === true)
    .map(f => f.replace('include_', '').replace(/^\w/, c => c.toUpperCase()))
  if (addressFields.length > 0) {
    field('Address Fields:', addressFields.join(', '))
  }

  // ══════════════════════════════════════════════════════════════════════
  // 4. Vendor Planning
  // ══════════════════════════════════════════════════════════════════════

  const configEntries = Object.entries(vendorConfig)
  if (configEntries.length > 0) {
    sectionHeader('4. Vendor Planning')

    for (const [code, config] of configEntries) {
      const vc = config as VendorCountryConfig
      if (!vc.vendors || vc.vendors.length === 0) continue
      checkPage(16)
      subHeader(`${countryName(code)} (${code})`)
      field('Vendors:', vc.vendors.map(v => vendorName(v)).join(', '))
      if (vc.match_criteria?.match_type) {
        field('Match Type:', MATCH_TYPE_OPTIONS.find(o => o.id === vc.match_criteria.match_type)?.label || vc.match_criteria.match_type)
      }
      if (vc.match_criteria?.fuzziness_notes) {
        field('Fuzziness Notes:', vc.match_criteria.fuzziness_notes)
      }
      if (vc.notes) field('Notes:', vc.notes)
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // 5. Execution
  // ══════════════════════════════════════════════════════════════════════

  const hasExecution = poc.execution_method || tickets.length > 0 || requests.length > 0 || poc.sample_test_notes
  if (hasExecution) {
    sectionHeader('5. Execution')

    if (poc.execution_method) {
      const em = EXECUTION_METHODS.find(m => m.id === poc.execution_method)
      field('Execution Method:', em ? `${em.label} - ${em.description}` : poc.execution_method)
    }

    // Jira tickets
    if (tickets.length > 0) {
      subHeader('Jira Tickets')
      const tw = contentWidth / 5
      table(
        ['Key', 'Type', 'Status', 'Assignee', 'Notes'],
        tickets.map((t: JiraTicketRef) => [t.key || '-', t.type, t.status, t.assignee || '-', t.notes || '-']),
        [tw, tw, tw * 0.7, tw * 0.8, tw * 1.5]
      )
    }

    // External requests
    if (requests.length > 0) {
      subHeader('External Vendor Requests')
      const rw = contentWidth / 5
      table(
        ['Vendor', 'Type', 'Status', 'Sent Date', 'Notes'],
        requests.map((r: ExternalRequest) => [r.vendor || '-', r.type, r.status, r.sent_date || '-', r.notes || '-']),
        [rw, rw, rw * 0.7, rw * 0.8, rw * 1.5]
      )
    }

    // Sample test
    field('Sample Test Complete:', poc.sample_test_complete ? 'Yes' : 'No')
    if (poc.sample_test_notes) fieldBlock('Sample Test Notes:', poc.sample_test_notes)
  }

  // ══════════════════════════════════════════════════════════════════════
  // 6. Results
  // ══════════════════════════════════════════════════════════════════════

  if (results && results.total_records > 0) {
    sectionHeader('6. Results Summary')

    field('Total Records:', results.total_records)
    field('Total Countries:', results.total_countries)
    if (results.overall_best_single) {
      field('Best Single Vendor:', `${results.overall_best_single.vendor} (${results.overall_best_single.rate}%)`)
    }
    if (results.overall_best_cascade) {
      field('Best Cascade:', `${results.overall_best_cascade.vendors.join(' + ')} (${results.overall_best_cascade.rate}%)`)
    }

    // Per-country vendor results
    subHeader('Per-Country Vendor Pass Rates')
    const vendorRows: string[][] = []
    for (const [country, data] of Object.entries(results.per_country)) {
      const cd = data as CountryResults
      for (const [vendor, stats] of Object.entries(cd.vendors)) {
        const s = stats as VendorPassRate
        const isBest = vendor === cd.best_single?.vendor
        vendorRows.push([country, vendor, String(s.pass), String(s.fail), String(s.total), `${s.rate}%`, isBest ? 'Best' : ''])
      }
    }
    if (vendorRows.length > 0) {
      const vw = contentWidth / 7
      table(
        ['Country', 'Vendor', 'Pass', 'Fail', 'Total', 'Rate', ''],
        vendorRows,
        [vw * 1.2, vw * 1.2, vw * 0.7, vw * 0.7, vw * 0.7, vw * 0.8, vw * 0.7]
      )
    }

    // Cascade results
    const cascadeRows: string[][] = []
    for (const [country, data] of Object.entries(results.per_country)) {
      const cd = data as CountryResults
      if (!cd.cascades) continue
      const bestSingleRate = cd.best_single?.rate || 0
      for (const [cascade, stats] of Object.entries(cd.cascades)) {
        const s = stats as VendorPassRate
        const gain = Math.round((s.rate - bestSingleRate) * 10) / 10
        cascadeRows.push([country, cascade, `${s.rate}%`, gain > 0 ? `+${gain}%` : '-'])
      }
    }
    if (cascadeRows.length > 0) {
      subHeader('Cascade Combinations')
      const cw = contentWidth / 4
      table(
        ['Country', 'Cascade', 'Pass Rate', 'Gain vs Best Single'],
        cascadeRows,
        [cw, cw * 1.4, cw * 0.8, cw * 0.8]
      )
    }
  }

  // ── Footer ───────────────────────────────────────────────────────────

  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(7)
    doc.setTextColor(150, 150, 150)
    doc.text(
      `POC Report: ${poc.company_name || poc.deal_name} | Generated by DealHub | ${new Date().toLocaleDateString('en-AU')} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    )
  }

  const filename = `POC-Report-${(poc.company_name || poc.deal_name || 'export').replace(/[^a-zA-Z0-9]+/g, '-')}.pdf`
  doc.save(filename)
}
